import React, { useMemo, useState } from 'react';
import { Alert, Box, Link } from '@mui/material';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import {
  ACCOUNT_COLORS,
} from '../../../components/account/accountPageStyles';
import { AccountButton } from '../../../components/account/AccountButton';
import { explorerTxUrl, CWY_IS_MAINNET } from '../config/cwyConfig';
import { confirmTreasuryDeposit, type CwyTransferRequest } from '../services/walletService';
import { validateCwyRequestAmount } from '../utils/cwyAmountValidation';
import { buildCwyDepositTransaction } from '../utils/buildCwyDepositTransaction';
import { CwyAmountField, CwyFieldHint, CwyFieldLabel } from './CwyActionsCard';
import { CwyAmountPercentPicker } from './CwyAmountPercentPicker';
import { useWalletCwyBalance } from '../hooks/useWalletCwyBalance';
import {
  formatMissingCwyBalanceError,
  getWalletCwyBalance,
  formatInsufficientSolError,
} from '../utils/cwyTokenBalance';
import { formatNetworkMismatchError } from '../utils/walletNetworkCheck';

import type { WalletTransactionSigner } from '../hooks/useSolanaWallets';

interface CwyDepositPanelProps {
  canDeposit: boolean;
  walletAddress?: string;
  walletProviderLabel?: string;
  treasuryWallet?: string;
  depositing: boolean;
  connecting?: boolean;
  ensureWalletReady: () => Promise<WalletTransactionSigner>;
  onDeposited: (amount: number, deposit: CwyTransferRequest) => void;
  onDepositPending?: (amount: number) => void;
  onDepositStart: () => void;
  onDepositError: (message: string) => void;
}

export const CwyDepositPanel: React.FC<CwyDepositPanelProps> = ({
  canDeposit,
  walletAddress,
  depositing,
  connecting,
  ensureWalletReady,
  onDeposited,
  onDepositPending,
  onDepositStart,
  onDepositError,
}) => {
  const { connection } = useConnection();
  const { publicKey, sendTransaction } = useWallet();
  const { balance, ataExists, solBalance, loading: balanceLoading, refresh: refreshBalance } =
    useWalletCwyBalance(walletAddress, Boolean(walletAddress));
  const [amountInput, setAmountInput] = useState('1');
  const [localError, setLocalError] = useState<string | null>(null);
  const [pendingConfirmation, setPendingConfirmation] = useState<{
    amount: number;
    txSignature: string;
  } | null>(null);

  const validationError = useMemo(() => validateCwyRequestAmount(amountInput), [amountInput]);
  const depositAmount = useMemo(() => Number(amountInput.trim()), [amountInput]);
  const insufficientBalance =
    balance != null && !Number.isNaN(depositAmount) && depositAmount > balance;
  const noCwyInWallet = balance != null && !ataExists;
  const insufficientSol = solBalance != null && solBalance < 0.001;
  const busy = depositing || connecting;
  const submitDisabled =
    !canDeposit ||
    busy ||
    balanceLoading ||
    balance === null ||
    Boolean(validationError) ||
    insufficientBalance ||
    noCwyInWallet ||
    insufficientSol;

  const recordDepositOnServer = async (amount: number, txSignature: string) => {
    const deposit = await confirmTreasuryDeposit({
      amount,
      tx_signature: txSignature,
    });
    setPendingConfirmation(null);
    setAmountInput('1');
    await refreshBalance();
    onDeposited(amount, deposit);
  };

  const handleConfirmPending = async () => {
    if (!pendingConfirmation) return;

    setLocalError(null);
    onDepositStart();
    try {
      await recordDepositOnServer(pendingConfirmation.amount, pendingConfirmation.txSignature);
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { message?: string } }; message?: string };
      const message =
        err.response?.status === 404
          ? 'Deposit recording endpoint is unavailable. Restart the API server and try again.'
          : err.response?.data?.message ||
            err.message ||
            'Could not record deposit. Try again in a moment.';
      setLocalError(message);
      onDepositError(message);
    }
  };

  const handleDeposit = async () => {
    const validation = validateCwyRequestAmount(amountInput);
    if (validation) {
      setLocalError(validation);
      return;
    }

    let signerPublicKey = publicKey;
    let signerSend = sendTransaction;

    if (!walletAddress) {
      setLocalError('No linked wallet on your account.');
      return;
    }

    if (!signerPublicKey || signerPublicKey.toBase58() !== walletAddress || !signerSend) {
      try {
        const ready = await ensureWalletReady();
        signerPublicKey = ready.publicKey;
        signerSend = ready.sendTransaction;
      } catch (e: unknown) {
        const err = e as { message?: string };
        const message = err.message || 'Could not connect your wallet. Try again.';
        setLocalError(message);
        onDepositError(message);
        return;
      }
    }

    const amount = Number(amountInput.trim());

    const onChainBalance = await getWalletCwyBalance(connection, signerPublicKey);
    if (onChainBalance.solBalance < 0.001) {
      const message = formatInsufficientSolError(onChainBalance.solBalance);
      setLocalError(message);
      onDepositError(message);
      return;
    }
    if (!onChainBalance.tokenAccount || onChainBalance.balance <= 0) {
      const message = formatMissingCwyBalanceError(walletAddress);
      setLocalError(message);
      onDepositError(message);
      return;
    }
    if (amount > onChainBalance.balance) {
      const message = `Insufficient CWY balance. You have ${onChainBalance.balance} CWY in this wallet.`;
      setLocalError(message);
      onDepositError(message);
      return;
    }

    setLocalError(null);
    onDepositStart();

    try {
      const transaction = await buildCwyDepositTransaction({
        connection,
        sender: signerPublicKey,
        amountCwy: amount,
      });

      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

      const signature = await signerSend(transaction, connection, {
        skipPreflight: false,
        preflightCommitment: 'confirmed',
      });

      await connection.confirmTransaction(
        { signature, blockhash, lastValidBlockHeight },
        'confirmed'
      );

      onDepositPending?.(amount);

      try {
        await recordDepositOnServer(amount, signature);
      } catch (confirmError: unknown) {
        const err = confirmError as {
          response?: { status?: number; data?: { message?: string } };
          message?: string;
        };
        setPendingConfirmation({ amount, txSignature: signature });
        const message =
          err.response?.status === 404
            ? 'CWY was sent on-chain, but the server could not record it yet. Use "Record deposit" below after the API is updated.'
            : err.response?.data?.message ||
              err.message ||
              'CWY was sent on-chain, but recording failed. Use "Record deposit" below to retry without sending again.';
        setLocalError(message);
        onDepositError(message);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      let message =
        err.response?.data?.message ||
        err.message ||
        'Deposit failed. Check your wallet balance and try again.';
      // Detect network mismatch (wallet on devnet, app on mainnet)
      const networkMsg = formatNetworkMismatchError(e);
      if (networkMsg) {
        message = networkMsg;
      } else if (message.includes('no record of a prior credit')) {
        message = formatMissingCwyBalanceError();
      }
      setLocalError(message);
      onDepositError(message);
    }
  };

  return (
    <Box>
      <CwyAmountPercentPicker
        balance={balance}
        disabled={!canDeposit || busy}
        onSelectAmount={(value) => {
          setAmountInput(value);
          setLocalError(null);
        }}
      />

      {insufficientSol && (
        <Alert
          severity="warning"
          sx={{
            mb: 2.25,
            borderRadius: ACCOUNT_COLORS.radiusSm,
            fontFamily: ACCOUNT_COLORS.fontBody,
            fontSize: 13,
            lineHeight: 1.55,
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          You have <strong>{solBalance ?? 0} SOL</strong> in this wallet. Solana requires a small
          amount of SOL to pay network fees — CWY alone is not enough.
          {CWY_IS_MAINNET ? (
            <> Add at least <strong>0.001 SOL</strong> to your wallet to cover transaction fees.</>
          ) : (
            <> On devnet, get free SOL from{' '}
              <Link href="https://faucet.solana.com" target="_blank" rel="noopener noreferrer">
                faucet.solana.com
              </Link>{' '}
              and send it to your linked wallet.</>
          )}
        </Alert>
      )}

      <CwyFieldLabel>Amount to deposit</CwyFieldLabel>
      <CwyAmountField
        value={amountInput}
        onChange={(value) => {
          setAmountInput(value);
          setLocalError(null);
        }}
        disabled={!canDeposit || busy || balanceLoading}
      />
      <CwyFieldHint>
        {localError ||
          validationError ||
          (noCwyInWallet
            ? 'No CWY detected in your linked wallet. Request CWY to that address first.'
            : insufficientSol
              ? CWY_IS_MAINNET
                ? 'Add SOL to cover transaction fees (see warning above).'
                : 'Add devnet SOL for transaction fees (see warning above).'
              : insufficientBalance
                ? `You only have ${balance} CWY available.`
                : balanceLoading
                  ? 'Loading CWY balance…'
                  : 'Minimum 1 CWY.')}
      </CwyFieldHint>

      <AccountButton
        accountVariant="gold"
        fullWidth
        onClick={() => void handleDeposit()}
        disabled={submitDisabled || Boolean(pendingConfirmation)}
        loading={busy}
        sx={{
          mb: 2,
          minHeight: 48,
          fontSize: 14,
          fontWeight: 700,
          color: '#fff',
          '&:not(.Mui-disabled)': { color: '#fff' },
        }}
      >
        Deposit to CWY
      </AccountButton>

      {pendingConfirmation && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
          On-chain deposit sent ({pendingConfirmation.amount} CWY).{' '}
          <Link
            href={explorerTxUrl(pendingConfirmation.txSignature)}
            target="_blank"
            rel="noopener noreferrer"
          >
            View transaction
          </Link>
          <Box sx={{ mt: 1.5 }}>
            <AccountButton
              accountVariant="primary"
              fullWidth
              onClick={() => void handleConfirmPending()}
              loading={depositing}
              sx={{ minHeight: 40 }}
            >
              Record deposit
            </AccountButton>
          </Box>
        </Alert>
      )}

    </Box>
  );
};
